import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import SkylineHero from "@/components/SkylineHero";

const Pillars = () => {
  const { t } = useLanguage();
  const heroRef = useScrollAnimation();
  const pillarsRef = useScrollAnimation();

  const pillars = [
    { 
      name: t("pillars.homes"), 
      path: "/portfolio/homes",
      description: "Luxury serviced apartments and aparthotels designed for modern living",
      number: "01"
    },
    { 
      name: t("pillars.hotels"), 
      path: "/portfolio/hotels",
      description: "Premium hospitality experiences that redefine accommodation standards", 
      number: "02"
    },
    { 
      name: t("pillars.developments"), 
      path: "/portfolio/developments",
      description: "Innovative real estate development projects that shape communities",
      number: "03" 
    },
    { 
      name: t("pillars.technologies"), 
      path: "/portfolio/technologies",
      description: "Cutting-edge technology solutions driving digital transformation",
      number: "04"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section ref={heroRef.ref} className={`py-32 px-6 bg-white relative overflow-hidden transition-all duration-1000 ${heroRef.isVisible ? 'animate-fade-in' : 'opacity-0'}`}>
        {/* Skyline drawing animation — matches hotels hero */}
        <SkylineHero className="opacity-20" />
        <div className="max-w-6xl mx-auto text-center">
          {/* Back button */}
          <Link 
            to="/"
            className="inline-flex items-center text-black/70 hover:text-black transition-all duration-300 mb-16 hover-scale group"
          >
            <ArrowLeft className="mr-2 transition-transform duration-300 group-hover:-translate-x-1" size={20} />
            Back to Home
          </Link>

          <div className="space-y-8">
            <h1 className="text-6xl md:text-7xl lg:text-8xl font-light text-black mb-6 tracking-tight">
              Our Pillars
            </h1>
            <div className="w-24 h-px bg-black mx-auto"></div>
            <p className="text-2xl font-light text-black/80 max-w-4xl mx-auto leading-relaxed">
              Four distinct verticals, unified by excellence and innovation in every aspect of hospitality and real estate
            </p>
          </div>
        </div>
      </section>

      {/* Pillars Section */}
      <section ref={pillarsRef.ref} className={`py-20 px-6 bg-white transition-all duration-1000 ${pillarsRef.isVisible ? 'animate-fade-in' : 'opacity-0'}`}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16">
            {pillars.map((pillar, index) => (
              <Link
                key={pillar.name}
                to={pillar.path}
                className="group block"
              >
                <div className="bg-white border border-black/10 rounded-2xl p-12 hover:border-black/30 transition-all duration-500 hover:shadow-lg min-h-[350px] flex flex-col justify-between">
                  {/* Header */}
                  <div>
                    <div className="flex items-start justify-between mb-8">
                      <div className="text-6xl font-extralight text-black/20 group-hover:text-black/40 transition-colors duration-500">
                        {pillar.number}
                      </div>
                      <div className="w-10 h-10 rounded-full border border-black/20 flex items-center justify-center group-hover:border-black group-hover:bg-black transition-all duration-300">
                        <ArrowRight className="w-5 h-5 text-black/40 group-hover:text-white transition-colors duration-300" />
                      </div>
                    </div>
                    
                    <div className="space-y-6">
                      <h2 className="text-3xl md:text-4xl font-medium text-black group-hover:text-black/90 transition-colors duration-300">
                        {pillar.name}
                      </h2>
                      <div className="w-16 h-px bg-black transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="mt-8">
                    <p className="text-lg text-black/70 leading-relaxed">
                      {pillar.description}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-black/5 rounded-2xl p-16">
            <h3 className="text-3xl md:text-4xl font-light text-black mb-6">
              Excellence in Every Vertical
            </h3>
            <p className="text-xl text-black/80 leading-relaxed">
              Each pillar represents our commitment to innovation, quality, and sustainable growth across diverse sectors of the hospitality and real estate industry.
            </p>
          </div>
        </div>
      </section>

      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default Pillars;